// This file contains unfortunate data transformations
// which require the use of 'any' type to work around.
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
    ContentFile,
    ContentItem,
    ContentUrl,
    ExperienceListingResponse,
    LogoSetItem,
    PublisherLogoItem,
    SCHEMA_VERSION,
} from '@oneappexchange/oaf-schema';
import { produce } from 'immer';
import { LISTING_SERVICE_API_VERSION, LISTING_SERVICE_URL, USE_STATIC_DATA } from '~/modules/config';
import { generateMediaUrl } from '~/modules/utilities/asset_utils';
import { shouldTransformResponse } from '~/modules/utilities/axios_utilities';
import { CORRELATION_ID_PARAM, logger, randomUUID } from '~/modules/utilities/cross_env_utils';
import { handleException } from '~/modules/utilities/error_utilities';
import { HttpRequestConfig, http } from '~/modules/utilities/http';
import { randomIntBetween } from '~/modules/utilities/number_utils';

const iconMap = {
    File: 'notebook',
    Url: 'new_window',
};

const LISTING_SECURITY_REVIEW_REQUIRED_MESSAGE = 'listing_security_review_required';

/**
 * OAF guarantees restricted listing-detail responses as
 * `404 { message: "listing_security_review_required" }`. Keep both checks so
 * unrelated 404s continue to render the generic not-found experience.
 */
export function isSecurityReviewRequiredListingError(error: unknown): boolean {
    const response = (error as { response?: { status?: unknown; data?: unknown } })?.response;
    if (response?.status !== 404) {
        return false;
    }

    let responseData = response.data;
    if (typeof responseData === 'string') {
        try {
            responseData = JSON.parse(responseData);
        } catch {
            return false;
        }
    }

    return (
        typeof responseData === 'object' &&
        responseData !== null &&
        (responseData as { message?: unknown }).message === LISTING_SECURITY_REVIEW_REQUIRED_MESSAGE
    );
}

export interface ListingRequestOptions {
    /** Set false when a request-scoped caller owns the structured error log. */
    logErrors?: boolean;
    /** Aborts the upstream request when the caller's deadline or lifecycle ends. */
    signal?: AbortSignal;
}
/**
 * Type containing the results of transformations done to the ExperienceListingResponse by the ListingService in appx-site package.
 */
export interface ListingResponse extends ExperienceListingResponse {
    tzId?: string;
    oafId?: string;
    appExchangeId?: string;
    external_id?: string;
    type: string;
    //TODO Define the real location of the more from this provider data
    moreFromThisProvider?: {
        id: string;
        listingUrl: string;
        averageRating: string;
        reviewsAmount: string;
        publisher: string;
        listingCategories: [];
        imageURL: string;
        title: string;
        description: string;
    };
}

export interface Resource {
    id: string;
    icon: string;
    caption: string;
    url: string;
    shouldDownload: boolean;
    fileName?: string;
}

export interface ResourceSection {
    type: string;
    resources: Resource[];
    column: number;
    barColor: string;
}

const resourceColumns: Record<number, number> = {
    0: 1,
    1: 2,
    2: 3,
    3: 1,
    4: 3,
    5: 2,
    6: 1,
    7: 3,
    8: 2,
};

/** Adds column and a unique random bar color to each section */
const addDynamicData = (sections: ResourceSection[]) => {
    const colors = ['#FF5D2D', '#DD7A01', '#3BA755', '#1B96FF', '#7F8CED', '#CA8501', '#06A59A', '#0D9DDA'];
    return sections.map((section, index) => {
        const colorPos = randomIntBetween(0, colors.length);
        return {
            ...section,
            column: resourceColumns[index],
            barColor: colors.splice(colorPos, 1)[0],
        };
    });
};

const buildResourceSection: (item: ContentItem, resourcePosition: number) => ResourceSection = (
    item: ContentItem,
    resourcePosition: number,
) => {
    const resourceSection: ResourceSection = {
        type: item.data.type,
        resources: [],
        column: resourceColumns[resourcePosition],
        barColor: '',
    };

    return resourceSection;
};

export const buildResource = (item: ContentItem) => {
    const resource: Resource = {
        id: item.external_id,
        icon: iconMap[item.type],
        caption: item.data.title,
        url: item.type === 'Url' ? (item.data as ContentUrl).url : generateMediaUrl((item.data as ContentFile).mediaId),
        shouldDownload: item.type === 'File',
        fileName: item.type === 'File' ? (item.data as ContentFile).fileName || item.data.title : undefined,
    };

    return resource;
};

// transforms are applied as plugin and extension arrays are processed;
// create extra transforms as needed using type as key
const transforms: any = {
    'listing/plugins/LogoSet': (element: any) => {
        const value: any = {};
        (element.data.items as LogoSetItem[])?.forEach((logo) => {
            value[logo.logoType] = logo.mediaId;
        });
        return value;
    },
    'publisher/plugins/PublisherLogo': (element: any) => {
        const value: any = {};
        (element.data.items as PublisherLogoItem[])?.forEach((logo) => {
            value[logo.logoType] = logo.mediaId;
        });
        return value;
    },
    'listing/plugins/Content': (element: any) => {
        const sections: any = {};
        (element.data.items as ContentItem[])?.forEach((item, index) => {
            if (!item.data.title) {
                return;
            }

            const section: ResourceSection = (sections[item.data.type] =
                sections[item.data.type] || buildResourceSection(item, index));

            section.resources.push(buildResource(item));
        });

        return addDynamicData(Object.values(sections));
    },
};

const config: HttpRequestConfig = {
    transformResponse: [
        (jsonData, _headers, status) => {
            if (!shouldTransformResponse(status)) {
                return jsonData;
            }
            return jsonData ? JSON.parse(jsonData) : undefined;
        },
        (data, _headers, status) => {
            if (!shouldTransformResponse(status)) {
                return data;
            }
            return data ? getListingResponse(data) : undefined;
        },
    ],
};

/**
 *  !!! WARNING !!! DO NOT EXTEND THIS FUNCTION
 *
 * THIS WAS A BAD PATTERN THAT IS NOW DIFFICULT TO UNDO
 */
export function getListingResponse(listingResponse: ExperienceListingResponse) {
    //ListingResponse contains all listing related data including publisher.
    //In the future it will be expanded to include review summary data and more.
    return produce(listingResponse, (draft: any) => {
        // new field used at Analtytics Instrumentation to explicitly indicate oafId listingId value
        draft.oafId = listingResponse.external_id;

        // change extensions and plugins from array to dictionary to make
        // easier to use; some elements may need transform in addition
        draft.plugins?.forEach((element: any) => {
            let elementData: any = element.data;
            if (transforms[element.pluginType]) {
                elementData = transforms[element.pluginType](element);
            }
            draft[element.pluginType] = elementData;
        });

        // TODO: I think we're no longer directly accessing extensions out of the listing response object, this code can be deleted?
        draft.extensions?.forEach((element: any) => {
            let elementData: any = element.data;
            if (transforms[element.extensionType]) {
                elementData = transforms[element.extensionType](element);
            }
            draft[element.extensionType] = elementData;
        });

        const publisher: any = draft.publisher;
        publisher?.plugins?.forEach((element: any) => {
            let elementData: any = element.data;
            if (transforms[element.pluginType]) {
                elementData = transforms[element.pluginType](element);
            }
            publisher[element.pluginType] = elementData;
        });

        // map the extensions to make data easier to access
        publisher?.extensions?.forEach((element: any) => {
            publisher[element.extensionType] = element.data;
        });
    }) as unknown as ListingResponse;
}

export class ListingService {
    getListing(
        id: string,
        oafCookie?: string,
        correlationId?: string,
        previewToken?: string,
        options: ListingRequestOptions = {},
    ): Promise<{ listing: ListingResponse; headers: AnyObject }> {
        // Treat the listing id as one opaque path segment. This prevents query/path injection for
        // server callers such as the skills/plugins BFF endpoints and prevents traversal through
        // the development-only dynamic import below.
        const encodedId = encodeURIComponent(id);
        let listingPromise: Promise<{ data: ListingResponse; headers: AnyObject }>;
        if (USE_STATIC_DATA) {
            listingPromise = import(`~/modules/services/__data__/${encodedId}.json`).then((result) => ({
                data: getListingResponse(result as ExperienceListingResponse),
                headers: {},
            }));
        } else {
            const headers: {
                Version: string;
                SchemaVersion: string;
                [CORRELATION_ID_PARAM]: string;
                oaf_canary?: string;
                'x-appx-preview-token'?: string;
            } = {
                Version: LISTING_SERVICE_API_VERSION || '1',
                SchemaVersion: SCHEMA_VERSION,
                [CORRELATION_ID_PARAM]: correlationId ?? randomUUID(),
            };

            if (oafCookie) {
                headers.oaf_canary = oafCookie;
            }

            if (previewToken) {
                headers['x-appx-preview-token'] = previewToken;
                logger.debug('listing_service.getListing: forwarding preview token', {
                    previewTokenPresent: true,
                    listingId: id,
                    correlationId,
                });
            }

            listingPromise = http.get(`${LISTING_SERVICE_URL}/${encodedId}`, {
                ...config,
                headers,
                // ListingService owns this log so it can classify anticipated OAF denials.
                suppressErrorLogging: true,
                signal: options.signal,
            });
        }

        return listingPromise
            .then(({ data, headers }) => {
                return { listing: data, headers: headers };
            })
            .catch((error) => {
                const isSecurityReviewRequired = isSecurityReviewRequiredListingError(error);
                if (options.logErrors !== false) {
                    handleException(error, 'listing_service.getListing', {
                        debugData: {
                            listingId: id,
                            correlationId,
                            oafCookiePresent: Boolean(oafCookie),
                            response: error?.response?.data,
                        },
                        // Logging decision guide Q3: this denial is anticipated, so it is a warning.
                        ...(isSecurityReviewRequired ? { level: 'warn' as const } : {}),
                    });
                }
                throw error;
            });
    }
}
